import { useEffect } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { DEFAULT_COUNTRY_CODE, SUPPORT_URL } from "app/modules/project-from-comparison/config";
import { useProjectCreation } from "app/modules/project-from-comparison/context";
import { projectCreationDataAutomation } from "app/modules/project-from-comparison/dataAutomation";
import { useCreateProject } from "app/modules/project-from-comparison/hooks/useCreateProject";
import projectCreatingTrack from "app/modules/project-from-comparison/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import * as SC from "./styles";
import { ERROR_MODAL_HEADER, ERROR_MODAL_BODY, ERROR_BUTTON_CONTACT_SUPPORT, ERROR_BUTTON_TRY_AGAIN } from "./text";

type ErrorModalSchema = {
    isOpen: boolean;
    onClose: () => void;
};

export const ErrorModal = ({ isOpen, onClose }: ErrorModalSchema) => {
    const { country = DEFAULT_COUNTRY_CODE } = proNavigator.getParams();
    const { isCreationEnabled, resetContext, domains, isWeeklyUpdatesEnabled, projectName } = useProjectCreation();
    const { onCreate, isProjectCreating } = useCreateProject({
        isCreationEnabled,
        country,
        isWeeklyUpdatesEnabled,
        domains,
        projectName,
    });
    const { translate } = useTranslate();

    const handleClose = () => {
        onClose();
        resetContext();
    };

    const goToSupport = () => {
        projectCreatingTrack.trackProjectCreationSystemErrorContactSupportClick();

        window.open(SUPPORT_URL, "_blank");

        onClose();
    };

    const onTryAgain = async () => {
        await onCreate({ onSuccess: handleClose });

        projectCreatingTrack.trackProjectCreationSystemErrorTryAgainClick();
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        projectCreatingTrack.trackProjectCreationSystemErrorShow();
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnEsc
            shouldCloseOnOverlayClick
            customStyles={{
                content: {
                    width: "400px",
                },
                wrapper: {
                    gap: "16px",
                },
            }}
            data-automation={projectCreationDataAutomation.errorModal.container}
        >
            <Modal.Header>
                <Modal.Title data-automation={projectCreationDataAutomation.errorModal.header}>
                    {translate(ERROR_MODAL_HEADER)}
                </Modal.Title>
            </Modal.Header>

            <SC.Container>
                <Text
                    fontVariant="body1"
                    color={colorsPalettes.carbon[400]}
                    data-automation={projectCreationDataAutomation.errorModal.body}
                >
                    {translate(ERROR_MODAL_BODY, { projectName })}
                </Text>
            </SC.Container>

            <Modal.Footer>
                <Button
                    type="flat"
                    onClick={goToSupport}
                    dataAutomation={projectCreationDataAutomation.errorModal.contactSupportButton}
                >
                    {translate(ERROR_BUTTON_CONTACT_SUPPORT)}
                </Button>

                <Button
                    onClick={onTryAgain}
                    dataAutomation={projectCreationDataAutomation.errorModal.tryAgainButton}
                    isLoading={isProjectCreating}
                >
                    {translate(ERROR_BUTTON_TRY_AGAIN)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
